<template>
  <div id="settingsServices">
    <div class="container">

      <Tabs>
        <TabSection :title="$t('settings.configure_emergency_settings')">
          <TipBox warning>
            <span v-html="$t('tips.emergency_close.description_kiosk')"></span>
          </TipBox>
          <div v-if="ready">
            <div class="feature" v-if="('emergencyCloseKioskManually' in config.emergency)">
              <span class="feature-name">{{ $t('settings.emergency_close') }}</span>
              <select v-model="config.emergency.emergencyCloseKioskManually">
                <option :value="true">{{ $t('yes') }}</option>
                <option :value="false">{{ $t('no') }}</option>
              </select>
            </div>
            <div class="feature" v-if="('emergencyCloseKioskManuallyMessage' in config.emergency)">
              <span class="feature-name">{{ $t('settings.emergency_close_message') }}</span>
              <LocalizedInput textarea :translate="config.emergency.emergencyCloseKioskManuallyMessage" :maxSize="200" />
            </div>
          </div>
        </TabSection>

        <TabSection tag="SuperAdmin" :title="$t('kiosk.access_management')" v-if="$store.getters.loggedUserHasSuperAdmin">
          <span class="subtitle-2">{{ $t('kiosk.generate_access') }}</span>
          <p>{{ $t('kiosk.generate_description') }}</p>
          <button class="btn btn-primary" @click="generateKioskAccess">{{ $t('generate') }}</button>
          <template v-if="apiKey">
            <br>
            <div class="columns">
              <div class="column" v-if="branch">
                <div class="group">
                  <label>{{ $t('kiosk.branch_canonical') }}</label>
                  <input type="text" readonly :value="branch.nameCanonical">
                </div>
              </div>
              <div class="column" v-if="company">
                <div class="group">
                  <label>{{ $t('kiosk.company_canonical') }}</label>
                  <input type="text" readonly :value="company.nameCanonical">
                </div>
              </div>
            </div>
            <label class="labelApiKey">API Key</label>
            <div class="apiKey">
              {{ apiKey }}
              <div class="icon" @click="copy">
                <svgicon src="icon/copy.svg" :height="20" :width="20"></svgicon>
              </div>
            </div>
          </template>
        </TabSection>

        <TabSection :title="$t('barcodes.title')">
          <TipBox warning>
            <span v-html="$t('barcodes.subtitle')"></span>
          </TipBox>
          <BarCodeSettings :barCodeSettings="barCodeSettings"></BarCodeSettings>
        </TabSection>
      </Tabs>

      <div class="footer-actions" v-if="$store.getters.loggedUserHasModifyPermission">
        <button class="btn btn-primary" @click="save()">{{ $t("save") }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import LocalizedInput from "@/components/tools/LocalizedInput.vue";
  import BarCodeSettings from "@/components/kiosk/BarCodeSettings.vue";

  export default {

    name: "SettingsServices",

    components: { BarCodeSettings, LocalizedInput },

    data() {
      return {
        config: {},
        emergency: { emergencyCloseKioskManually: false, emergencyCloseKioskManuallyMessage: { fr: "" } },
        barCodeSettings: {
          active: false,
          totalLenght: 0,
          ignoreLeadingZero: false,
          priceStartIndex: -1,
          priceLength: 0
        },
        branchId: null,
        companyId: null,
        apiKey: null,
        ready: false
      }
    },

    computed: {
      company() {
        return this.$store.state.contextCompany;
      },
      branch() {
        return this.$store.state.contextBranch;
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "settings.services";
      this.$store.state.pageIcon = "";
      this.branchId = this.$route.query.branch;
      this.companyId = this.$route.query.company;

      if (this.branchId && this.companyId) {
        await this.getCompanyConfig();
      } else {
        this.$router.push("/");
      }
    },

    methods: {
      ...mapActions(["getCompany", "saveCompany", "generateKioskLogin"]),

      async getCompanyConfig() {
        let config = await this.getCompany({ branchId: this.branchId, companyId: this.companyId, fallback: true });
        this.config = config.kiosk;
        this.config.emergency = this.config.emergency || this.emergency;
        if (config.barCodeSettings) {
          this.barCodeSettings = config.barCodeSettings;
        }
        this.ready = true;
      },

      async generateKioskAccess() {
        let response = await this.generateKioskLogin({ branchId: this.branchId });
        if (response) {
          this.apiKey = response.apiKey;
        }
      },

      validateBarcode() {
        return this.barCodeSettings && this.barCodeSettings.active && this.barCodeSettings.totalLenght && this.barCodeSettings.priceLength && this.barCodeSettings.priceStartIndex
          && this.barCodeSettings.priceStartIndex >= 0 && this.barCodeSettings.totalLenght >= (parseInt(this.barCodeSettings.priceStartIndex) + parseInt(this.barCodeSettings.priceLength));
      },

      copy() {
        if (!this.apiKey || !navigator.clipboard) {
          return;
        }
        navigator.clipboard.writeText(this.apiKey);
        toast(this.$t("copied"), "success");
      },

      async save() {
        if (this.branchId && this.companyId) {
          if (this.barCodeSettings && this.barCodeSettings.active && !this.validateBarcode()) {
            toast(this.$t("barcodes.error_titles"), "error");
            return;
          }

          let response = await this.saveCompany({
            branchId: this.branchId,
            companyId: this.companyId,
            data: { kiosk: this.config, barCodeSettings: this.barCodeSettings }
          });
          toast(this.$t("company.success"), "success");
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .feature {
    display: flex;
    align-items: center;
    margin-bottom: 10px;

    select {
      width: 170px;
      flex-shrink: 0;
    }

    textarea {
      width: 60%;
    }
  }

  .feature-name {
    flex-grow: 1;
    font-weight: 500;
  }

  .apiKey {
    margin-left: 0;
    margin-right: 0;
    text-align: center;
    font-weight: bold;
    letter-spacing: 1px;
    font-family: 'Roboto';
    border: 1px solid #e6e6e6;
    padding: 10px;
    border-radius: 10px;
    position: relative;

    .icon {
      padding: 10px;
      position: absolute;
      cursor: pointer;
      right: 0;
      top: 0;
    }
  }

  .labelApiKey {
    margin-top: 25px;
    margin-bottom: 4px;
  }
</style>
